import { Button } from "@/shared/components/shadcn/Button";
import { Card } from "@/shared/components/ui/Card";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { formatBusinessDate } from "@/shared/utils/formatters";
import { useAppointmentEvents } from "../hooks/appointmentQueries";
import type { AppointmentEvent } from "../types/appointmentEventTypes";

const actors: Record<AppointmentEvent["actorType"], string> = {
  owner: "Business owner",
  customer: "Customer",
  system: "System",
  unknown: "",
};

const statusTitles: Record<AppointmentEvent["toStatus"], string> = {
  pending: "Appointment pending",
  confirmed: "Appointment confirmed",
  completed: "Appointment completed",
  cancelled: "Appointment cancelled",
  declined: "Appointment declined",
  no_show: "Appointment marked as no show",
};

function eventTitle(event: AppointmentEvent) {
  if (event.eventType === "created") return "Appointment created";
  if (event.eventType === "rescheduled") return "Appointment rescheduled";
  return statusTitles[event.toStatus];
}

function HistoryEntry({ event, timeZone, currentYear }: {
  event: AppointmentEvent;
  timeZone: string;
  currentYear: string;
}) {
  const formatDate = (date: Date) => formatBusinessDate(date, timeZone, {
    year: formatBusinessDate(date, timeZone, { year: "numeric" }) === currentYear ? undefined : "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  const actor = actors[event.actorType];

  return (
    <li className="space-y-2 py-4 first:pt-0 last:pb-0">
      <p className="text-sm font-semibold">{eventTitle(event)}</p>
      <p className="text-xs text-muted-foreground">
        <time dateTime={event.createdAt.toISOString()}>{formatDate(event.createdAt)}</time>
        {actor && <> &middot; {actor}</>}
      </p>
      {event.eventType === "rescheduled" && (
        <div className="space-y-1 text-sm">
          <p>Previous: {event.oldSchedule?.startAt ? formatDate(event.oldSchedule.startAt) : "Start not recorded"}</p>
          <p>New: {event.newSchedule.startAt ? formatDate(event.newSchedule.startAt) : "Start not recorded"}</p>
          {event.oldSchedule && event.oldSchedule.locationId !== event.newSchedule.locationId && (
            <p>Appointment location changed.</p>
          )}
        </div>
      )}
      {event.reason && <p className="whitespace-pre-wrap break-words text-sm">Reason: {event.reason}</p>}
    </li>
  );
}

export function AppointmentHistory({ appointmentId, timeZone }: { appointmentId: string; timeZone: string }) {
  const query = useAppointmentEvents(appointmentId);
  const currentYear = formatBusinessDate(new Date(), timeZone, { year: "numeric" });

  return (
    <Section
      title="Appointment history"
      action={
        <Button variant="quiet" disabled={query.isFetching} onClick={() => void query.refetch()}>
          Refresh history
        </Button>
      }
    >
      {query.isLoading ? (
        <RowSkeleton />
      ) : query.error ? (
        <ErrorState title="Couldn't load appointment history" message={query.error.message} onRetry={() => void query.refetch()} />
      ) : !query.data?.length ? (
        <EmptyState message="No appointment history recorded." />
      ) : (
        <Card>
          <ol className="divide-y divide-border" aria-label="Appointment history events">
            {query.data.map((event) => (
              <HistoryEntry key={event.id} event={event} timeZone={timeZone} currentYear={currentYear} />
            ))}
          </ol>
        </Card>
      )}
    </Section>
  );
}
